import { useState, useRef, useEffect } from 'react'
import { Card, Row, Col, Form, Button, Spinner, Alert, Table, Modal, InputGroup, Badge } from 'react-bootstrap'
import { useQuery } from 'react-query'
import api from '../../services/api'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import { formatCurrency } from '../../utils/formatters'
import { formatDateOnlyEsMX, getMonthEndDateOnlyMx, getMonthStartDateOnlyMx } from '../../utils/apiDateFormat'

const AuxiliarCuentaReport = ({ initialClientId = '', initialAccountId = '', initialStartDate = '', initialEndDate = '' }) => {
  const { clientId: clienteId, setClientId: setClienteId, selectDisabled } = useSyncedLocalClientId(
    initialClientId ? String(initialClientId) : ''
  )
  const [accountId, setAccountId] = useState(initialAccountId)
  const [startDate, setStartDate] = useState(initialStartDate || getMonthStartDateOnlyMx())
  const [endDate, setEndDate] = useState(initialEndDate || getMonthEndDateOnlyMx())
  const [exporting, setExporting] = useState(false)
  
  // Estados para modal de búsqueda de cuentas
  const [mostrarModalCuenta, setMostrarModalCuenta] = useState(false)
  const [busquedaModalCuenta, setBusquedaModalCuenta] = useState('')
  const [resultadosModalCuenta, setResultadosModalCuenta] = useState([])
  const [buscandoModalCuenta, setBuscandoModalCuenta] = useState(false)
  const debounceTimeoutCuentaRef = useRef(null)
  const [selectedAccountInfo, setSelectedAccountInfo] = useState(null)

  // Obtener lista de clientes para el filtro
  const { data: clientesData, isLoading: isLoadingClients } = useQuery(
    'clients',
    async () => {
      const response = await api.get('/clients/clients/?activo=true')
      return response.data.results || response.data || []
    }
  )
  const clients = Array.isArray(clientesData) ? clientesData : []

  // Cargar información de la cuenta seleccionada cuando cambia accountId
  useQuery(
    ['account-info', accountId],
    async () => {
      if (!accountId) {
        setSelectedAccountInfo(null)
        return null
      }
      try {
        const response = await api.get(`/accounting/accounts/${accountId}/`)
        setSelectedAccountInfo(response.data)
        return response.data
      } catch (error) {
        console.error('Error cargando información de cuenta:', error)
        setSelectedAccountInfo(null)
        return null
      }
    },
    { enabled: !!accountId }
  )

  // Cargar información inicial si se pasa initialAccountId
  useEffect(() => {
    if (initialAccountId && !selectedAccountInfo) {
      api.get(`/accounting/accounts/${initialAccountId}/`)
        .then(response => {
          setSelectedAccountInfo(response.data)
        })
        .catch(error => {
          console.error('Error cargando información inicial de cuenta:', error)
        })
    }
  }, [initialAccountId, selectedAccountInfo])

  // Obtener datos del Auxiliar de Cuenta
  const { data: reportData, isLoading: isLoadingReport, error: reportError, refetch } = useQuery(
    ['auxiliar-cuenta-report', clienteId, accountId, startDate, endDate],
    async () => {
      const params = new URLSearchParams()
      if (clienteId) params.append('client_id', clienteId)
      if (accountId) params.append('account_id', accountId)
      if (startDate) params.append('fecha_inicio', startDate)
      if (endDate) params.append('fecha_fin', endDate)
      const response = await api.get(`/financial/reportes/auxiliar-cuenta/?${params.toString()}`)
      return response.data
    },
    { 
      enabled: !!accountId && !!startDate && !!endDate, // Solo habilitar la consulta si todos los campos requeridos están presentes
      onError: (err) => {
        console.error("Error al cargar Auxiliar de Cuenta", err)
      }
    }
  )

  // Abrir modal de búsqueda de cuentas
  const abrirModalCuenta = () => {
    setMostrarModalCuenta(true)
    if (selectedAccountInfo?.code) {
      setBusquedaModalCuenta(selectedAccountInfo.code)
      buscarCuentasModal(selectedAccountInfo.code)
    }
  }

  // Cerrar modal de búsqueda de cuentas
  const cerrarModalCuenta = () => {
    setMostrarModalCuenta(false)
    setBusquedaModalCuenta('')
    setResultadosModalCuenta([])
  }

  // Buscar cuentas contables en el modal
  const buscarCuentasModal = async (query) => {
    if (!query || query.trim().length < 2) {
      setResultadosModalCuenta([])
      return
    }

    // Limpiar timeout anterior
    if (debounceTimeoutCuentaRef.current) {
      clearTimeout(debounceTimeoutCuentaRef.current)
    }

    // Debounce de 300ms
    debounceTimeoutCuentaRef.current = setTimeout(async () => {
      try {
        setBuscandoModalCuenta(true)
        const params = new URLSearchParams({
          search: query.trim(),
          is_active: 'true'
        })
        if (clienteId) {
          params.append('cliente_id', clienteId)
        }

        const response = await api.get(`/accounting/accounts/?${params.toString()}`)
        const results = response.data.results || response.data || []
        setResultadosModalCuenta(results)
      } catch (error) {
        console.error('Error buscando cuentas contables:', error)
        setResultadosModalCuenta([])
      } finally {
        setBuscandoModalCuenta(false)
      }
    }, 300)
  }

  // Seleccionar cuenta contable del modal
  const seleccionarCuenta = (cuenta) => {
    if (!cuenta || !cuenta.id) {
      return
    }
    
    setAccountId(cuenta.id.toString())
    setSelectedAccountInfo({
      id: cuenta.id,
      code: cuenta.code,
      name: cuenta.name,
      account_type: cuenta.account_type
    })
    cerrarModalCuenta()
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!accountId) {
      return
    }
    refetch()
  }

  const handleExport = async (format) => {
    if (!reportData) return
    
    try {
      setExporting(true)
      const params = new URLSearchParams()
      if (clienteId) params.append('client_id', clienteId)
      if (accountId) params.append('account_id', accountId)
      if (startDate) params.append('fecha_inicio', startDate)
      if (endDate) params.append('fecha_fin', endDate)
      params.append('format', format)
      
      const response = await api.get(`/financial/reportes/exportar/auxiliar-cuenta/?${params.toString()}`, {
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      const accountCode = reportData?.account?.code || accountId || 'cuenta'
      link.setAttribute('download', `Auxiliar_Cuenta_${accountCode}.${format === 'excel' ? 'xlsx' : 'pdf'}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      console.error('Error exportando:', error)
    } finally {
      setExporting(false)
    }
  }

  if (isLoadingClients) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando datos...</p>
      </div>
    )
  }

  return (
    <div>
      <Card className="mb-4">
        <Card.Header as="h5">Auxiliar de Cuenta</Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group controlId="formClient">
                  <Form.Label>Cliente</Form.Label>
                  <Form.Select
                    value={clienteId}
                    onChange={(e) => setClienteId(e.target.value)}
                    disabled={selectDisabled}
                  >
                    <option value="">Todos los clientes</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.razon_social || client.business_name || `${client.nombre} ${client.apellido_paterno || ''}`}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="formAccount">
                  <Form.Label>Cuenta Contable <span className="text-danger">*</span></Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="text"
                      value={selectedAccountInfo ? `${selectedAccountInfo.code} - ${selectedAccountInfo.name}` : ''}
                      placeholder="Seleccione una cuenta contable"
                      readOnly
                      required
                      style={{ flex: 1 }}
                    />
                    <Button
                      variant="outline-primary"
                      onClick={abrirModalCuenta}
                      type="button"
                    >
                      <i className="bi bi-search"></i> Buscar
                    </Button>
                  </div>
                  {!accountId && (
                    <Form.Text className="text-danger">
                      Debe seleccionar una cuenta contable
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="formStartDate">
                  <Form.Label>Fecha Inicio <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="formEndDate">
                  <Form.Label>Fecha Fin <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col className="d-flex gap-2">
                <Button variant="primary" type="submit" disabled={isLoadingReport || !accountId}>
                  {isLoadingReport ? <Spinner animation="border" size="sm" /> : 'Generar Reporte'}
                </Button>
                <Button variant="outline-danger" onClick={() => handleExport('pdf')} disabled={!reportData || exporting}>
                  {exporting ? <Spinner animation="border" size="sm" /> : 'Exportar PDF'}
                </Button>
                <Button variant="outline-success" onClick={() => handleExport('excel')} disabled={!reportData || exporting}>
                  {exporting ? <Spinner animation="border" size="sm" /> : 'Exportar Excel'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {reportError && (
        <Alert variant="danger">
          Error al cargar el reporte: {reportError.message}
        </Alert>
      )}

      {!reportData && !isLoadingReport && accountId && startDate && endDate && (
        <Alert variant="info">
          Haga clic en &apos;Generar Reporte&apos; para ver los datos.
        </Alert>
      )}

      {reportData && (
        <Card>
          <Card.Header as="h6">
            Auxiliar de Cuenta: {reportData.account?.code} - {reportData.account?.name}
            {reportData.client && (
              <span className="ms-2 text-muted">({reportData.client.name})</span>
            )}
          </Card.Header>
          <Card.Body>
            {/* Información de saldos */}
            <Row className="mb-4">
              <Col md={6}>
                <Card className="bg-light">
                  <Card.Body>
                    <Row>
                      <Col>
                        <strong>Saldo Inicial:</strong>
                      </Col>
                      <Col className="text-end">
                        <strong>{formatCurrency(reportData.saldo_inicial || 0)}</strong>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="bg-light">
                  <Card.Body>
                    <Row>
                      <Col>
                        <strong>Saldo Final:</strong>
                      </Col>
                      <Col className="text-end">
                        <strong>{formatCurrency(reportData.saldo_final || 0)}</strong>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Tabla de movimientos */}
            <div className="table-responsive">
              <Table striped bordered hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Referencia</th>
                    <th>Descripción</th>
                    <th className="text-end">Debe</th>
                    <th className="text-end">Haber</th>
                    <th className="text-end">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.movimientos && reportData.movimientos.length > 0 ? (
                    reportData.movimientos.map((mov) => (
                      <tr key={mov.id}>
                        <td>{mov.date ? formatDateOnlyEsMX(mov.date, {}, '') : ''}</td>
                        <td>{mov.reference || '-'}</td>
                        <td>{mov.description || '-'}</td>
                        <td className="text-end">{mov.debit > 0 ? formatCurrency(mov.debit) : '-'}</td>
                        <td className="text-end">{mov.credit > 0 ? formatCurrency(mov.credit) : '-'}</td>
                        <td className="text-end"><strong>{formatCurrency(mov.balance || 0)}</strong></td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">
                        No hay movimientos en el período seleccionado
                      </td>
                    </tr>
                  )}
                  {reportData.totales && (
                    <tr className="table-info">
                      <td colSpan="3"><strong>TOTALES</strong></td>
                      <td className="text-end"><strong>{formatCurrency(reportData.totales.debit || 0)}</strong></td>
                      <td className="text-end"><strong>{formatCurrency(reportData.totales.credit || 0)}</strong></td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Modal de búsqueda de cuentas contables */}
      <Modal
        show={mostrarModalCuenta}
        onHide={cerrarModalCuenta}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Buscar Cuenta Contable</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Buscar por código o nombre</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Escribe para buscar (mínimo 2 caracteres)..."
                value={busquedaModalCuenta}
                onChange={(e) => {
                  setBusquedaModalCuenta(e.target.value)
                  buscarCuentasModal(e.target.value)
                }}
                autoFocus
              />
              {buscandoModalCuenta && (
                <InputGroup.Text>
                  <Spinner animation="border" size="sm" />
                </InputGroup.Text>
              )}
            </InputGroup>
            <Form.Text className="text-muted">
              Busca por código de cuenta o por nombre
            </Form.Text>
          </Form.Group>

          {resultadosModalCuenta.length > 0 ? (
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <Table striped bordered hover size="sm">
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '150px' }}>Código</th>
                    <th>Nombre</th>
                    <th style={{ width: '120px' }}>Tipo</th>
                    <th style={{ width: '100px' }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {resultadosModalCuenta.map((cuenta) => (
                    <tr key={cuenta.id}>
                      <td><strong>{cuenta.code}</strong></td>
                      <td>
                        <div style={{ fontSize: '0.9rem' }}>
                          {cuenta.name}
                        </div>
                        {cuenta.description && (
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {cuenta.description.substring(0, 50)}...
                          </div>
                        )}
                      </td>
                      <td>
                        {cuenta.account_type && (
                          <Badge bg="info" style={{ fontSize: '0.75rem' }}>
                            {cuenta.account_type}
                          </Badge>
                        )}
                      </td>
                      <td className="text-center">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => seleccionarCuenta(cuenta)}
                        >
                          Seleccionar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : busquedaModalCuenta.trim().length >= 2 && !buscandoModalCuenta ? (
            <Alert variant="info" className="mb-0">
              No se encontraron resultados para &quot;{busquedaModalCuenta}&quot;
            </Alert>
          ) : busquedaModalCuenta.trim().length < 2 ? (
            <Alert variant="secondary" className="mb-0">
              Escribe al menos 2 caracteres para comenzar la búsqueda
            </Alert>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cerrarModalCuenta}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default AuxiliarCuentaReport
